import clsx from 'clsx';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { type Components as ReactMarkdownComponents } from 'react-markdown/src/ast-to-react';
import gfm from 'remark-gfm';

import type { MarkdownProps } from './markdown-types.js';
import { MarkdownWrapperCSS } from './Markdown.sty.js';
import { tableMappings } from './transformers/table-mapping.js';
import { typographyMappings } from './transformers/typography-mapping.js';
import { findValidParent, findOffset, extractSourcePos } from './utils.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { Components } from '../../core/types/markdown/react-markdown.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';

const COMPONENT_NAME = 'Markdown';

/**
 * @public
 */
export const componentMappings: Components = {
  ...typographyMappings,
  ...tableMappings,
};

/**
 * The `Markdown` component is a read-only way to display content formatted in
 * markdown-style.
 * @public
 */
export const Markdown = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MarkdownProps
>((props, forwardedRef) => {
  const {
    children,
    onSelectionChange,
    customComponentMappings,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const startNode = useRef<Element | null>(null);
  const endNode = useRef<Element | null>(null);

  const extendedComponentMappings = useMemo(() => {
    return customComponentMappings
      ? {
          ...componentMappings,
          ...customComponentMappings,
        }
      : componentMappings;
  }, [customComponentMappings]);

  const handleSelectionChange = () => {
    const selection = document.getSelection();

    if (
      !selection ||
      selection.isCollapsed ||
      !selection.anchorNode ||
      !selection.focusNode
    ) {
      return;
    }

    const start = findValidParent(selection.anchorNode);
    const end = findValidParent(selection.focusNode);

    if (!start && !end) {
      return;
    }

    if (start) {
      startNode.current = start;
    }
    if (end) {
      endNode.current = end;
    }

    if (startNode.current && endNode.current) {
      const sourcePos = extractSourcePos(startNode.current, endNode.current);
      if (sourcePos && onSelectionChange) {
        const startOffset = findOffset(sourcePos.start, children, -1);
        const endOffset = findOffset(sourcePos?.end, children);
        sourcePos.start.offset = startOffset;
        sourcePos.end.offset = endOffset;

        onSelectionChange(sourcePos);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return (
    <div
      ref={forwardedRef}
      className={clsx(
        textStyleCSS({ textStyle: 'base' }),
        MarkdownWrapperCSS,
        consumerClassName,
      )}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      <ReactMarkdown
        components={extendedComponentMappings as ReactMarkdownComponents}
        remarkPlugins={[gfm as never]}
        skipHtml={true}
        sourcePos
      >
        {children}
      </ReactMarkdown>
    </div>
  );
});

(Markdown as typeof Markdown & { displayName: string }).displayName =
  COMPONENT_NAME;
